import React, { useEffect, useState } from 'react'
import {
  Box,
  Grid2 as Grid,
  IconButton,
  Typography,
  CircularProgress
} from '@mui/material'
import { getPatientPains, getPatientSymptoms } from 'services/symptoms_tracking'
import { useParams } from 'react-router-dom'
import {
  IPatientPainsResponse,
  IPatientSymptomResponse,
  PainsType
} from 'types'
import ButtonBase from 'design-system/button'
import { ClearIcon, DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import SelectBox from 'components/SelectBox'
import TimeLine from 'components/TimeLine'
import { useToast } from 'services/context/toast'
import { FloatingActionButton } from 'design-system/FloatingActionButton'
import AddIcon from '@mui/icons-material/Add'
import SymptomsModal from './components/SymptomsModal'

type TimeLineData = Array<IPatientPainsResponse | IPatientSymptomResponse>

const painsMap: Record<PainsType, string> = {
  Head: 'Cabeça',
  Limbs: 'Braços e/ou pernas',
  Back: 'Costas',
  Chest: 'Peito',
  Abdomen: 'Abdômen',
  Neck: 'Pescoço',
  Generalized: 'Generalizada',
  Other: 'Outro'
}

const serviceAction = {
  pain: getPatientPains,
  symptom: getPatientSymptoms
}

const SymptomsTrackingPage: React.FC = () => {
  const [state, setState] = useState<IStateStdTypes>('idle')
  const { patientId } = useParams<{ patientId: string }>()
  const [timelineItems, setTimelineItems] = useState<TimeLineData>([])
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null)
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null)
  const [selectedType, setSelectedType] = useState<string>('all')
  const [errors, setErrors] = useState<{ pain: boolean; symptom: boolean }>({
    pain: false,
    symptom: false
  })
  const [modalOpen, setModalOpen] = useState(false)

  const toast = useToast()

  const fetchServiceData = async (
    dataType: 'pain' | 'symptom',
    reset: boolean = false
  ) => {
    if (reset || selectedType === 'all' || selectedType === dataType) {
      const response = await serviceAction[dataType](patientId, {
        event_date_from: !reset && startDate ? startDate.toDate() : undefined,
        event_date_to: !reset && endDate ? endDate.toDate() : undefined
      })

      if (response.success) {
        return response.data ?? []
      } else {
        setErrors(prev => ({ ...prev, [dataType]: true }))
      }
    }
    return []
  }

  const fetchData = async (reset: boolean = false) => {
    if (state === 'loading') return
    setState('loading')

    const symptoms = await fetchServiceData('symptom', reset)
    const pains = await fetchServiceData('pain', reset)

    const timelineList: TimeLineData = symptoms

    timelineList.push(...pains)

    timelineList.sort((a, b) => {
      const dateA = new Date(a.event_date).getTime()
      const dateB = new Date(b.event_date).getTime()
      return dateB - dateA
    })

    setTimelineItems(timelineList)

    setState('success')
  }

  const resetFilters = () => {
    setStartDate(null)
    setEndDate(null)
    setSelectedType('all')
    fetchData(true)
  }

  const handleModalSubmit = () => {
    setModalOpen(false)
    fetchData()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (Object.values(errors).every(err => err)) {
      toast.handler({ severity: 'error', content: 'Erro ao buscar dados.' })
    } else if (errors.pain) {
      toast.handler({ severity: 'error', content: 'Erro ao buscar dores.' })
    } else if (errors.symptom) {
      toast.handler({ severity: 'error', content: 'Erro ao buscar sintomas.' })
    }
  }, [errors, toast])

  return (
    <Grid container spacing={2} direction='column' width='100%' padding={2}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant='h5'>
            Acompanhamento de Sintomas e Dores
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }} sx={{ position: 'relative' }}>
          <DatePicker
            label='Data início'
            value={startDate}
            onChange={newValue => setStartDate(newValue)}
            maxDate={endDate ?? dayjs()}
            sx={{ width: '100%' }}
          />
          {startDate && (
            <IconButton
              size='small'
              onClick={() => setStartDate(null)}
              sx={{
                position: 'absolute',
                right: 40,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1
              }}
            >
              <ClearIcon fontSize='small' />
            </IconButton>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }} sx={{ position: 'relative' }}>
          <DatePicker
            label='Data fim'
            value={endDate}
            onChange={newValue => setEndDate(newValue?.endOf('day') || null)}
            minDate={startDate || undefined}
            maxDate={dayjs()}
            sx={{ width: '100%' }}
          />
          {endDate && (
            <IconButton
              size='small'
              onClick={() => setEndDate(null)}
              sx={{
                position: 'absolute',
                right: 40,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1
              }}
            >
              <ClearIcon fontSize='small' />
            </IconButton>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }} sx={{ position: 'relative' }}>
          <SelectBox
            label='Evento'
            menuItems={[
              { label: 'Todos', value: 'all' },
              { label: 'Dor', value: 'pain' },
              { label: 'Sintoma', value: 'symptom' }
            ]}
            fullWidth
            control={[selectedType, setSelectedType]}
          />
          {selectedType != 'all' && (
            <IconButton
              size='small'
              onClick={() => setSelectedType('all')}
              sx={{
                position: 'absolute',
                right: 40,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1
              }}
            >
              <ClearIcon fontSize='small' />
            </IconButton>
          )}
        </Grid>
        <Grid
          size={{ xs: 12, sm: 6, lg: 3 }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2
          }}
        >
          <ButtonBase
            variant='outlined'
            onClick={resetFilters}
            disabled={state === 'loading'}
            loading={state === 'loading'}
          >
            Limpar filtro
          </ButtonBase>
          <ButtonBase
            variant='contained'
            onClick={() => fetchData()}
            disabled={state === 'loading'}
            loading={state === 'loading'}
          >
            Aplicar filtro
          </ButtonBase>
        </Grid>
      </Grid>
      {state === 'loading' ? (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          minHeight='200px'
        >
          <CircularProgress />
        </Box>
      ) : (
        <TimeLine
          timelineItems={timelineItems}
          leftLabel={item => formatDate(item.event_date.toString())}
          rightLabel={item => {
            if ('symptoms_name' in item) {
              return (
                <Grid container direction={'column'}>
                  <Typography fontWeight={'bold'}>Sintoma</Typography>
                  <Typography>{item.symptoms_name}</Typography>
                </Grid>
              )
            }
            return (
              <Grid container direction={'column'}>
                <Typography fontWeight={'bold'}>Dor</Typography>
                <Typography>
                  {painsMap[item.location ?? 'Other']} - nível {item.severity}
                </Typography>
              </Grid>
            )
          }}
          groupBy={item => new Date(item.event_date).toDateString()}
        />
      )}
      <FloatingActionButton
        sx={{
          position: 'sticky',
          bottom: '5%',
          float: 'right',
          alignSelf: 'flex-start',
          margin: '16px'
        }}
        onClick={() => setModalOpen(true)}
        aria-label='Adicionar registro de dor/sintoma'
      >
        <AddIcon />
      </FloatingActionButton>

      <SymptomsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleModalSubmit}
      />
    </Grid>
  )
}

export default SymptomsTrackingPage
